/**
 * Design tokens — Material 2 "Classic".
 *
 * These are the ONLY place platform colours, radii and layout constants are defined.
 * Screens never hard-code a colour or a pixel value; they read the theme. That is what
 * makes the theme swappable later (Classic → Material 3 / dense / dark) in one file
 * without touching a single screen.
 */

export const LAYOUT = {
  /**
   * Permanent navigation drawer width — 280px, the SOURCE's measurement
   * (`apr_template/src/App.css:2538`, `margin-left: 280px`).
   *
   * It was 256 until Application Evaluation landed. That bucket is the first three
   * levels deep, and the source's own indent rule (`16 + depth * 16`) plus an icon at
   * depth 2 left too little room: "APR Model & Execution" and "Disposition Intelligence"
   * both truncated to an ellipsis, while every shallower bucket rendered fine. They fit
   * in APR because APR's sidebar is 24px wider.
   *
   * CLAUDE.md §1.7 makes the chrome around the nav the source's, reproduced exactly, so
   * matching the width is the faithful fix rather than shortening labels or shrinking
   * the indent.
   */
  drawerWidth: 280,
  /** App bar height — matches Material's desktop standard. */
  appBarHeight: 64,
  /**
   * Height of the anchored vendor footer, reserved as bottom padding on the content
   * area so no screen's last row can hide behind it. A single token because the band
   * and the padding that clears it must never disagree.
   */
  footerHeight: 40,
  /** Base spacing unit; the 8px grid every screen aligns to. */
  spacingUnit: 8,
  /** Cards sit on a slightly larger radius than controls, as in Material 2. */
  cardRadius: 8,
  controlRadius: 4,
  /**
   * Reading width, for screens that choose to constrain themselves — prose, forms,
   * settings. The shell does NOT apply this: data screens use the full window, as the
   * source applications do. A screen that wants it opts in.
   */
  contentMaxWidth: 1440,
} as const;

/**
 * THE FIELD — the platform's one label-and-control pattern.
 *
 * Core design, chosen by the owner on 2026-09-13 from the two that existed: Project
 * Creation's compact box and the Execution Control Center's rounded one. The rounded shape
 * won, at the roomier size.
 *
 * These are TOKENS rather than literals inside one component because the pattern is meant to
 * be the platform's, not one screen's. Before this, the same picker existed five times with
 * five different widths and four different labels, and nothing said which was right. A screen
 * that needs a field reads from here; if the design changes, it changes once.
 *
 * `design/index.html` documents this pattern under "Form fields"; `SelectField` implements it.
 * If the two ever disagree, this file is the authority — the reference page is transcribed by
 * hand and can drift.
 */
/**
 * THE PROMPT TEXTS. One wording for one idea, imported rather than retyped.
 *
 * Before this there were six spellings of the same prompt across nineteen screens —
 * `Select Project`, `-- Select Project --`, `Select a project…`, `Choose a delivery project…`,
 * `— Select a work item project —`. They are one string now, and a screen that needs it
 * imports it, so the next one cannot drift.
 *
 * `allProjects` / `allOpportunities` are NOT prompts — they are real choices, and they stay
 * distinct precisely so nobody replaces a working "show me everything" filter with a
 * "you have not chosen yet" placeholder. The two mean opposite things.
 */
/**
 * The value a picker carries when NOTHING has been chosen yet.
 *
 * It exists because '' was already taken. On several screens '' is a REAL choice — "All
 * Projects", "All (project-wide)", "Global (all)" — so a field sitting at '' displayed that
 * choice, and the prompt could never be shown. Giving "not chosen" its own value lets the two
 * coexist: the prompt is always the first row, and "All …" keeps its own row underneath.
 *
 * A screen treats UNSET as whatever it did before when its value was '' — so what LOADS is
 * unchanged; only what the FIELD READS changes.
 */
export const UNSET = '__unset__';

export const PROMPT = {
  project: '— Select a Project —',
  opportunity: '— Select an Opportunity —',
  allProjects: 'All Projects',
  allOpportunities: 'All Opportunities',
} as const;

export const FIELD = {
  /** Label: beside its control, never above it, and never wrapped. */
  labelFontSize: 13,
  // 600, not 500 — copied from the canonical source, not chosen:
  // ExecutionControlCenter.css `.pm-ecc .dl-fg label`.
  labelFontWeight: 600,
  labelColor: '#455a64',
  /** Gap between a label and its control on one row. */
  labelGap: 8,

  /**
   * The control's height. CLAUDE.md §1.9.2 rules 36px for a text field or select, and a
   * read-only box that sits BESIDE one has to be the same height — so the number lives
   * here, read by both, rather than being typed twice and drifting apart.
   */
  controlHeight: 36,

  /** The control — rounded, 6px, roomier than a bare input. */
  controlPadding: '7px 12px',
  controlRadius: 6,
  controlFontSize: 13,
  controlBorder: '#d5dde3',
  controlBackground: '#ffffff',
  controlColor: '#1a1a1a',

  /** Invalid state. */
  errorColor: '#8c1d18',
  /** Quiet helper text under a control. */
  helpFontSize: 11.5,
  helpColor: '#5f6368',

  /**
   * NAMED WIDTHS. One width for every screen cannot work — a four-control toolbar needs a
   * compact picker, a list of projects named in full sentences needs a wide one. A screen
   * declares intent (`width="lg"`) instead of typing a pixel value nobody can interpret later.
   */
  widths: {
    /** Compact — a toolbar holding several controls. Codes and short names. */
    sm: '180px',
    /** Default. A name plus a short qualifier. */
    md: '280px',
    /** Descriptive names — the project and opportunity pickers. */
    lg: '380px',
    /** Long sentences, where the whole value genuinely has to be readable. */
    xl: '520px',
    /** Fills its container — a form column rather than a toolbar. */
    full: '100%',
  },
} as const;

export const PALETTE_LIGHT = {
  primary: { main: '#1565c0', dark: '#0d47a1', light: '#5e92f3', contrastText: '#ffffff' },
  secondary: { main: '#7b1fa2', dark: '#4a0072', light: '#ae52d4', contrastText: '#ffffff' },
  success: { main: '#2e7d32' },
  warning: { main: '#ed6c02' },
  error: { main: '#d32f2f' },
  info: { main: '#0288d1' },
  background: { default: '#f4f6f8', paper: '#ffffff' },
  divider: 'rgba(0, 0, 0, 0.12)',
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
} as const;

export const PALETTE_DARK = {
  primary: { main: '#90caf9', dark: '#42a5f5', light: '#c3fdff', contrastText: 'rgba(0,0,0,0.87)' },
  secondary: {
    main: '#ce93d8',
    dark: '#9c64a6',
    light: '#ffc4ff',
    contrastText: 'rgba(0,0,0,0.87)',
  },
  success: { main: '#66bb6a' },
  warning: { main: '#ffa726' },
  error: { main: '#f44336' },
  info: { main: '#4fc3f7' },
  background: { default: '#121212', paper: '#1e1e1e' },
  divider: 'rgba(255, 255, 255, 0.12)',
  text: {
    primary: 'rgba(255, 255, 255, 0.92)',
    secondary: 'rgba(255, 255, 255, 0.7)',
    disabled: 'rgba(255, 255, 255, 0.5)',
  },
} as const;

/**
 * APR's declared stack, verbatim from the computed styles of its own screens.
 *
 * Aptos is a Microsoft face and is not installed here, so it falls through to IBM
 * Plex Sans, then Source Sans Pro, then the system UI font — which is what actually
 * renders. Declaring the whole chain matters anyway: install Aptos and the platform
 * matches APR exactly, without a code change.
 *
 * NOTE: APR's data GRID declares a different, system-only stack. That is reproduced
 * on the grid itself rather than here.
 */
export const FONT_FAMILY = [
  'Aptos',
  '"IBM Plex Sans"',
  '"Source Sans Pro"',
  '-apple-system',
  'system-ui',
  '"Segoe UI"',
  '"Helvetica Neue"',
  'Roboto',
  'Arial',
  'sans-serif',
].join(', ');

/** The grid's own stack — APR's tables declare this, without Aptos. */
export const FONT_FAMILY_DATA = [
  '-apple-system',
  'system-ui',
  '"Segoe UI"',
  'Roboto',
  'Oxygen',
  'Ubuntu',
  'Cantarell',
  '"Fira Sans"',
  '"Droid Sans"',
  '"Helvetica Neue"',
  'sans-serif',
].join(', ');
